import api from "./api";

export type FiscalEnvironment = "homologacao" | "producao";

export interface FiscalIssuer {
  id?: string | null;
  unitType?: number | null;
  cnpj: string;
  name: string;
  tradeName?: string | null;
  stateRegistration?: string | null;
  /** CRT: 1 Simples, 2 Simples excesso, 3 Normal. */
  taxRegime: number;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  cityCode?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  environment: FiscalEnvironment;
  enabled: boolean;
  /** Só leitura: motivo que impede emitir (token ausente/ilegível). */
  blocker?: string | null;
  blockerCode?: "no_token" | "token_unreadable" | "production_locked" | null;
  /** Só leitura: de onde vem o token de cada ambiente (o token nunca volta da API). */
  homologationTokenSource?: FiscalTokenSource | null;
  productionTokenSource?: FiscalTokenSource | null;
  entryDefaults?: FiscalEntryDefaults | null;
  shipmentDefaults?: FiscalShipmentDefaults | null;
  saleDefaults?: FiscalSaleDefaults | null;
}

/** server = variável do servidor (vale por cima); saved = salvo na tela; unreadable = salvo em outro servidor. */
export type FiscalTokenSource = "server" | "saved" | "unreadable";

export type FiscalInvoiceStatus = "draft" | "processing" | "authorized" | "rejected" | "denied" | "cancelled";

export interface FiscalInvoiceListItem {
  id: string;
  status: FiscalInvoiceStatus;
  operation: string;
  issuerName?: string | null;
  issuerCnpj?: string | null;
  issuerEnvironment?: FiscalEnvironment | null;
  /** Ambiente da nota: o do envio; rascunho/recusada = o atual do emitente. */
  environment?: FiscalEnvironment | null;
  number?: number | null;
  series?: number | null;
  accessKey?: string | null;
  recipientName?: string | null;
  total: number;
  processId?: string | null;
  processRef?: string | null;
  sourceInvoiceId?: string | null;
  container?: string | null;
  createdDate: string;
  authorizedAt?: string | null;
}

const errorsOf = (e: any, fallback: string) => {
  const data = e?.response?.data;
  const list: string[] = data?.errors ?? data?.notifications ?? [];
  return new Error(list.length ? list.join("\n") : fallback);
};

export const getFiscalIssuers = async (): Promise<FiscalIssuer[]> => (await api.get("/api/fiscal/issuers")).data;

export const saveFiscalIssuer = async (issuer: FiscalIssuer): Promise<FiscalIssuer> => {
  try {
    return (await api.put("/api/fiscal/issuers", issuer)).data;
  } catch (e) {
    throw errorsOf(e, "Não foi possível salvar o emitente.");
  }
};

/** Salva (a API testa na Focus) ou remove (token vazio) o token de um ambiente. */
export const saveFiscalIssuerToken = async (issuerId: string, environment: FiscalEnvironment, token: string | null): Promise<FiscalIssuer> => {
  try {
    return (await api.put(`/api/fiscal/issuers/${issuerId}/token`, { environment, token })).data;
  } catch (e) {
    throw errorsOf(e, "Não foi possível salvar o token.");
  }
};

export interface FiscalHomologationCleanup {
  count: number;
  authorized: number;
  blockers: string[];
}

export const getHomologationCleanup = async (issuerId: string): Promise<FiscalHomologationCleanup> =>
  (await api.get(`/api/fiscal/issuers/${issuerId}/homologation-invoices`)).data;

export const deleteHomologationInvoices = async (issuerId: string): Promise<string> => {
  try {
    return ((await api.delete(`/api/fiscal/issuers/${issuerId}/homologation-invoices`)).data?.notifications ?? []).join(" ");
  } catch (e) {
    throw errorsOf(e, "Não foi possível apagar as notas de teste.");
  }
};

export const getFiscalInvoices = async (params: { issuerId?: string; status?: string; processId?: string; search?: string } = {}): Promise<FiscalInvoiceListItem[]> =>
  (await api.get("/api/fiscal/invoices", { params })).data;

// ── Notas (detalhe/rascunho) ──────────────────────────────────────────────────

export interface FiscalEntryDefaults {
  natureOperation: string;
  cfop: string;
  onBehalf: boolean;
  iiInProductValue: boolean;
  origin: number;
  icmsCst: string;
  icmsBaseMode: number;
  icmsDeferralPercent: number;
  benefitCode?: string | null;
  ipiCst: string;
  ipiLegalCode: string;
  pisCofinsCst: string;
  ibsCbsCst: string;
  ibsCbsClassification: string;
  ibsStateRate: number;
  ibsCityRate: number;
  cbsRate: number;
  freightMode: number;
  unit: string;
  volumeKind: string;
  customsLocation: string;
  customsState: string;
  fiscoTextTemplate: string;
}

export interface FiscalIcmsProfile {
  name: string;
  cst: string;
  rate: number;
  baseReduction: number;
  benefitCode?: string | null;
  presumedCreditCode?: string | null;
  presumedCreditRate: number;
}

export interface FiscalShipmentDefaults {
  natureOperation: string;
  cfop: string;
  interstateCfop: string;
  pisCofinsCst: string;
  ipiCst: string;
  ipiLegalCode: string;
  ibsCbsCst: string;
  ibsCbsClassification: string;
  freightMode: number;
  defaultProfile: string;
  icmsProfiles: FiscalIcmsProfile[];
  fiscoTextTemplate: string;
  childNatureOperation: string;
  childIcmsCst: string;
  childIpiCst: string;
  childBenefitCode?: string | null;
  childFiscoTextTemplate: string;
}

export interface FiscalItemTaxes {
  icmsCst?: string | null;
  icmsDeferralPercent?: number | null;
  icmsValue?: number | null;
  ipiCst?: string | null;
  ipiBase?: number | null;
  ipiRate?: number | null;
  ipiValue?: number | null;
  iiBase?: number | null;
  iiValue?: number | null;
  pisCst?: string | null;
  pisRate?: number | null;
  pisValue?: number | null;
  cofinsCst?: string | null;
  cofinsRate?: number | null;
  cofinsValue?: number | null;
  ibsCbsBase?: number | null;
  ibsStateValue?: number | null;
  cbsValue?: number | null;
  [key: string]: any;
}

export interface FiscalInvoiceItem {
  itemNumber: number;
  processItemId?: string | null;
  productCode: string;
  description: string;
  ncm: string;
  cest?: string | null;
  cfop: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  grossValue: number;
  freight: number;
  insurance: number;
  discount: number;
  otherExpenses: number;
  origin: number;
  benefitCode?: string | null;
  additionalInfo?: string | null;
  taxes: FiscalItemTaxes;
  importDeclaration?: any;
}

export interface FiscalTotals {
  products: number;
  freight: number;
  insurance: number;
  discount: number;
  otherExpenses: number;
  ipi: number;
  ii: number;
  pis: number;
  cofins: number;
  icmsSt: number;
  icms?: number;
  fcpSt?: number;
  total: number;
}

export interface FiscalInvoiceEvent {
  type: string;
  httpStatus?: number | null;
  response?: string | null;
  userName?: string | null;
  createdDate: string;
}

export interface FiscalInvoice {
  id: string;
  ref: string;
  status: FiscalInvoiceStatus;
  issuerId: string;
  issuerName?: string | null;
  issuerCnpj?: string | null;
  issuerEnvironment?: FiscalEnvironment | null;
  /** Ambiente da nota: o do envio; rascunho/recusada = o atual do emitente. */
  environment?: FiscalEnvironment | null;
  processId?: string | null;
  processRef?: string | null;
  operation: string;
  natureOperation: string;
  direction: number;
  purpose: number;
  destination: number;
  finalConsumer: boolean;
  buyerPresence: number;
  freightMode: number;
  paymentMethod: string;
  iiInProductValue: boolean;
  volumeQuantity?: number | null;
  volumeKind?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  recipientName?: string | null;
  recipientDocument?: string | null;
  recipientCountry?: string | null;
  recipientCountryCode?: string | null;
  recipientState?: string | null;
  referencedKeys?: string | null;
  additionalInfo?: string | null;
  fiscoInfo?: string | null;
  number?: number | null;
  series?: number | null;
  accessKey?: string | null;
  sefazStatus?: string | null;
  sefazMessage?: string | null;
  carrierName?: string | null;
  carrierDocument?: string | null;
  sourceInvoiceId?: string | null;
  container?: string | null;
  hasXml: boolean;
  hasDanfe: boolean;
  lastErrors?: string | null;
  createdDate: string;
  createdByName?: string | null;
  authorizedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  hasCancellationXml?: boolean;
  /** Cancelamento pedido sem resposta definitiva: notas filhas barradas até o "Atualizar status" confirmar. */
  cancelRequestedAt?: string | null;
  /** Quem está vendo pode cancelar, mandar CC-e e e-mail. */
  canManage?: boolean;
  correctionLetters?: FiscalCorrectionLetter[];
  totals: FiscalTotals;
  events: FiscalInvoiceEvent[];
  items: FiscalInvoiceItem[];
  canEdit: boolean;
  [key: string]: any;
}

export interface FiscalInvoiceResult {
  success: boolean;
  errors: string[];
  warnings: string[];
  invoice?: FiscalInvoice | null;
}

export interface ImportEntryExpense {
  id: string;
  title: string;
  amount: number;
  isForecast: boolean;
  selected: boolean;
}

export interface ImportEntryPreview {
  processId: string;
  processRef: string;
  duimpNumber?: string | null;
  issuers: FiscalIssuer[];
  suggestedIssuerId?: string | null;
  expenses: ImportEntryExpense[];
  exporterName?: string | null;
  exporterCode?: string | null;
  recipientCountry?: string | null;
  recipientCountryCode?: string | null;
  customsLocation?: string | null;
  customsState?: string | null;
  knownCustomsUnits: string[];
  fiscoText?: string | null;
  itemCount: number;
  totals: FiscalTotals;
  warnings: string[];
  existing: FiscalInvoiceListItem[];
}

export interface ImportEntryRequest {
  issuerId: string;
  expenseIds: string[];
  exporterName?: string | null;
  exporterCode?: string | null;
  recipientCountry?: string | null;
  recipientCountryCode?: string | null;
  customsLocation?: string | null;
  customsState?: string | null;
  fiscoText?: string | null;
}

/** Resultado da API, inclusive quando ela responde 400 com { success:false, errors } no corpo. */
const resultOf = async (call: () => Promise<any>): Promise<FiscalInvoiceResult> => {
  try {
    const data = (await call()).data;
    // FiscalInvoiceResult traz "errors"; BaseResult (excluir) traz "notifications"
    return { warnings: [], ...data, errors: data.errors ?? data.notifications ?? [] };
  } catch (e: any) {
    const data = e?.response?.data;
    const errors = Array.isArray(data?.errors) ? data.errors : Array.isArray(data?.notifications) ? data.notifications : null;
    if (errors) return { success: false, errors, warnings: data.warnings ?? [], invoice: data.invoice };
    return { success: false, errors: ["Não foi possível falar com o servidor."], warnings: [] };
  }
};

export const getFiscalInvoice = async (id: string): Promise<FiscalInvoice> => (await api.get(`/api/fiscal/invoices/${id}`)).data;

export const saveFiscalDraft = (id: string, draft: FiscalInvoice) => resultOf(() => api.put(`/api/fiscal/invoices/${id}`, draft));

export const deleteFiscalDraft = (id: string) => resultOf(() => api.delete(`/api/fiscal/invoices/${id}`));

export const issueFiscalInvoice = (id: string) => resultOf(() => api.post(`/api/fiscal/invoices/${id}/issue`));

export const refreshFiscalInvoice = (id: string) => resultOf(() => api.post(`/api/fiscal/invoices/${id}/refresh`));

export const getImportEntryPreview = async (processId: string, issuerId?: string): Promise<ImportEntryPreview> =>
  (await api.get(`/api/process/${processId}/fiscal/import-entry`, { params: { issuerId } })).data;

export const createImportEntry = (processId: string, request: ImportEntryRequest) =>
  resultOf(() => api.post(`/api/process/${processId}/fiscal/import-entry`, request));

/** Baixa um arquivo da API com o nome que o servidor manda (Content-Disposition). */
const downloadBlob = async (path: string, fallbackName: string, params?: Record<string, unknown>) => {
  const response = await api.get(path, { responseType: "blob", params });
  const disposition = response.headers["content-disposition"] ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : plain ?? fallbackName;
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export type FiscalFileKind = "xml" | "danfe" | "cancel-xml" | "cce-xml" | "cce-pdf";

/** CC-e: informar o nº da carta (seq). */
export const downloadFiscalFile = (id: string, kind: FiscalFileKind, seq?: number) =>
  downloadBlob(`/api/fiscal/invoices/${id}/files/${kind}`, kind === "danfe" || kind === "cce-pdf" ? "nfe.pdf" : "nfe.xml", seq ? { seq } : undefined);

// ── Cancelamento, carta de correção, e-mail, inutilização ────────────────────

export interface FiscalCorrectionLetter {
  sequence: number;
  text: string;
  userName?: string | null;
  createdDate: string;
  hasXml: boolean;
  hasPdf: boolean;
}

export const cancelFiscalInvoice = (id: string, justification: string) =>
  resultOf(() => api.post(`/api/fiscal/invoices/${id}/cancel`, { justification }));

export const sendCorrectionLetter = (id: string, text: string) =>
  resultOf(() => api.post(`/api/fiscal/invoices/${id}/correction-letter`, { text }));

export interface FiscalEmailPreview {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  attachments: string[];
}

export const getFiscalEmailPreview = async (id: string): Promise<FiscalEmailPreview> =>
  (await api.get(`/api/fiscal/invoices/${id}/email`)).data;

export const sendFiscalEmail = (id: string, email: { to: string[]; cc: string[]; subject: string; body: string }) =>
  resultOf(() => api.post(`/api/fiscal/invoices/${id}/email`, email));

export interface FiscalNumberVoid {
  id: string;
  environment: FiscalEnvironment;
  series: number;
  startNumber: number;
  endNumber: number;
  justification: string;
  status: "authorized" | "rejected" | "unknown";
  sefazStatus?: string | null;
  sefazMessage?: string | null;
  errors?: string | null;
  hasXml: boolean;
  userName?: string | null;
  createdDate: string;
}

export interface FiscalNumberVoidResult {
  success: boolean;
  errors: string[];
  void?: FiscalNumberVoid | null;
}

export const getNumberVoids = async (issuerId: string): Promise<FiscalNumberVoid[]> =>
  (await api.get(`/api/fiscal/issuers/${issuerId}/voids`)).data;

/** Recusa também volta com o registro (a tentativa fica na lista). */
export const voidNumbers = async (issuerId: string, request: { series: number; startNumber: number; endNumber: number; justification: string }): Promise<FiscalNumberVoidResult> => {
  try {
    return (await api.post(`/api/fiscal/issuers/${issuerId}/voids`, request)).data;
  } catch (e: any) {
    const data = e?.response?.data;
    if (Array.isArray(data?.errors)) return { success: false, errors: data.errors, void: data.void };
    return { success: false, errors: ["Não foi possível falar com o servidor."] };
  }
};

export const downloadVoidXml = (id: string) => downloadBlob(`/api/fiscal/voids/${id}/xml`, "inutilizacao.xml");

// ── Remessa por conta e ordem (NF mãe) e NFs filhote ─────────────────────────

export interface FiscalParty {
  document: string;
  name: string;
  stateRegistration?: string | null;
  ieIndicator: number;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  cityCode?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  email?: string | null;
  saved?: boolean;
}

export interface ShipmentItem {
  itemNumber: number;
  productCode: string;
  description: string;
  ncm: string;
  unit: string;
  quantity: number;
  cost: number;
  ipiRate: number;
}

export interface ShipmentPreview {
  entryId: string;
  entryNumber?: number | null;
  entryAuthorized: boolean;
  processRef?: string | null;
  defaults: FiscalShipmentDefaults;
  recipient?: FiscalParty | null;
  items: ShipmentItem[];
  volumeQuantity?: number | null;
  volumeKind?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  fiscoText?: string | null;
  warnings: string[];
  existing: FiscalInvoiceListItem[];
}

export interface ShipmentRequest {
  recipient: FiscalParty;
  carrier?: FiscalParty | null;
  freightMode: number;
  volumeQuantity?: number | null;
  volumeKind?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  pisCofinsCst?: string | null;
  profile: string;
  itemProfiles: { itemNumber: number; profile: string }[];
  fiscoText?: string | null;
}

export interface ChildItem {
  itemNumber: number;
  description: string;
  unit: string;
  quantity: number;
  remaining: number;
}

export interface ChildrenPreview {
  motherId: string;
  motherNumber?: number | null;
  motherAuthorized: boolean;
  items: ChildItem[];
  containers: string[];
  volumeKind?: string | null;
  existing: FiscalInvoiceListItem[];
  warnings: string[];
}

export interface ChildRequest {
  container?: string | null;
  volumeQuantity?: number | null;
  volumeKind?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  items: { itemNumber: number; quantity: number }[];
}

/** Cadastro fiscal salvo para o CNPJ/CPF (null = não tem). */
export const getFiscalParty = async (document: string): Promise<FiscalParty | null> => {
  const response = await api.get(`/api/fiscal/parties/${document.replace(/\D/g, "")}`);
  return response.status === 204 ? null : response.data;
};

export const getShipmentPreview = async (entryId: string): Promise<ShipmentPreview> =>
  (await api.get(`/api/fiscal/invoices/${entryId}/shipment`)).data;

export const createShipment = (entryId: string, request: ShipmentRequest) =>
  resultOf(() => api.post(`/api/fiscal/invoices/${entryId}/shipment`, request));

export const getChildrenPreview = async (motherId: string): Promise<ChildrenPreview> =>
  (await api.get(`/api/fiscal/invoices/${motherId}/children`)).data;

export const createChildren = async (motherId: string, children: ChildRequest[]): Promise<{ success: boolean; errors: string[]; invoiceIds: string[] }> => {
  try {
    return (await api.post(`/api/fiscal/invoices/${motherId}/children`, { children })).data;
  } catch (e: any) {
    const data = e?.response?.data;
    return { success: false, errors: Array.isArray(data?.errors) ? data.errors : ["Não foi possível falar com o servidor."], invoiceIds: [] };
  }
};

// ── Venda (própria e encomenda) ──────────────────────────────────────────────

export interface FiscalSaleDefaults {
  natureOperation: string;
  onOrderNatureOperation: string;
  ipiCst: string;
  ipiLegalCode: string;
  pisCofinsCst: string;
  ibsCbsCst: string;
  ibsCbsClassification: string;
  origin: number;
  freightMode: number;
  paymentMethod: string;
  buyerPresence: number;
  fiscoTextTemplate?: string | null;
}

export interface SaleInstallment {
  number?: string | null;
  /** ISO completo (yyyy-MM-ddT00:00:00). */
  dueDate: string;
  /** Null = divide o que falta do total. */
  value?: number | null;
}

export interface SaleLine {
  sourceItemNumber?: number | null;
  productCode: string;
  description: string;
  ncm: string;
  cest?: string | null;
  unit: string;
  quantity: number;
  unitPrice: number;
  origin?: number | null;
  additionalInfo?: string | null;
}

export interface SaleRequest {
  issuerId: string;
  billingSettingId: string;
  sourceInvoiceId?: string | null;
  companyId?: string | null;
  branchId?: string | null;
  recipient: FiscalParty;
  carrier?: FiscalParty | null;
  freightMode: number;
  freight: number;
  insurance: number;
  discount: number;
  otherExpenses: number;
  volumeQuantity?: number | null;
  volumeKind?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  paymentMethod: string;
  installments: SaleInstallment[];
  customerOrder?: string | null;
  fiscoText?: string | null;
  additionalInfo?: string | null;
  items: SaleLine[];
}

export interface FiscalInstallment {
  number: string;
  dueDate: string;
  value: number;
}

export interface SalePreview {
  success: boolean;
  errors: string[];
  warnings: string[];
  draft?: (Omit<FiscalInvoice, "id" | "ref" | "status" | "events" | "totals" | "canEdit" | "hasXml" | "hasDanfe" | "createdDate"> & {
    installments: FiscalInstallment[];
  }) | null;
  totals: FiscalTotals;
  fiscoTemplateText?: string | null;
}

export interface SaleSourceItem {
  itemNumber: number;
  productCode: string;
  description: string;
  ncm: string;
  cest?: string | null;
  unit: string;
  origin: number;
  quantity: number;
  remaining: number;
  unitCost: number;
  additionalInfo?: string | null;
}

export interface SaleSource {
  entryId: string;
  entryNumber?: number | null;
  issuerId: string;
  issuerName?: string | null;
  processId?: string | null;
  processRef?: string | null;
  clientCompanyId?: string | null;
  clientName?: string | null;
  clientCnpj?: string | null;
  authorizedAt?: string | null;
  entryAuthorized: boolean;
  recipient?: FiscalParty | null;
  items: SaleSourceItem[];
  existing: FiscalInvoiceListItem[];
  warnings: string[];
}

export interface SaleProduct {
  productCode: string;
  description: string;
  ncm: string;
  cest?: string | null;
  unit: string;
  origin: number;
  unitCost: number;
  additionalInfo?: string | null;
  entryNumber?: number | null;
  processRef?: string | null;
}

export const getSaleInvoices = async (params: { status?: string; search?: string } = {}): Promise<FiscalInvoiceListItem[]> =>
  (await api.get("/api/fiscal/invoices", { params: { ...params, operations: "sale|sale_on_order" } })).data;

export const getSaleSources = async (): Promise<SaleSource[]> => (await api.get("/api/fiscal/sales/sources")).data;

export const getSaleSource = async (entryId: string): Promise<SaleSource> => (await api.get(`/api/fiscal/sales/sources/${entryId}`)).data;

export const getSaleProducts = async (issuerId: string, search: string): Promise<SaleProduct[]> =>
  (await api.get("/api/fiscal/sales/products", { params: { issuerId, search } })).data;

export const previewSale = async (request: SaleRequest, invoiceId?: string | null): Promise<SalePreview> =>
  (await api.post("/api/fiscal/sales/preview", request, { params: { invoiceId: invoiceId ?? undefined } })).data;

export const createSale = (request: SaleRequest) => resultOf(() => api.post("/api/fiscal/sales", request));

export const updateSale = (id: string, request: SaleRequest) => resultOf(() => api.put(`/api/fiscal/sales/${id}`, request));

export const getSaleRequest = async (id: string): Promise<SaleRequest> => (await api.get(`/api/fiscal/sales/${id}/request`)).data;
